/*
 Copyright 2019-present wobe-systems GmbH

 Licensed under the Apache License, Version 2.0 (the 'License');
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

     http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an 'AS IS' BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.
*/
import { ActionTree } from 'vuex';
import * as types from '@/store/types';
import { logging } from '@/scripts/debugger';
import { handleErrorMessage, handleInfoMessage } from '@/store/handleCatchError';
import { OK, SERVICES } from '@/middlewares/httpClient';
import i18n from '@/middlewares/i18n';
import { RootState } from '../models';
import { TypeLibrary, TypeState } from './models';
import {
  AddType, DeleteType, GetTypes, UpdateType, ResolvedTypes, ResolvedType,
} from './apis';

const service : string = SERVICES.get('flowmanager');

const log = (message: string) => {
  logging('VUEX/typeLibrary/actions', message);
};

export const actions: ActionTree<TypeState, RootState> = {
  async [types.FETCH_TYPES_LIBRARY]({ commit, dispatch }, workspaceId: string): Promise<any> {
    await GetTypes(workspaceId)
      .then(({ data }) => {
        const typesLibrary: TypeLibrary[] = data;

        commit(types.MUTATE_FETCH_TYPES_LIBRARY, typesLibrary);
      })
      .catch((err) => handleErrorMessage(err, dispatch, service));
  },

  async [types.ADD_TYPE_LIBRARY]({ commit, dispatch },
    { workspaceId, newType }: { workspaceId: string, newType: TypeLibrary }): Promise<any> {
    return new Promise((resolve, reject) => {
      AddType(workspaceId, newType)
        .then(({ data }) => {
          const typeLibrary: TypeLibrary = data;

          commit(types.MUTATE_ADD_TYPE_LIBRARY, typeLibrary);
          resolve(typeLibrary);
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },

  async [types.DELETE_TYPE_LIBRARY]({ commit, dispatch },
    { workspaceId, typeId }: { workspaceId: string, typeId: string }): Promise<any> {
    return new Promise((resolve, reject) => {
      DeleteType(workspaceId, typeId)
        .then(({ status }) => {
          if (status === OK) {
            commit(types.MUTATE_DELETE_TYPE_LIBRARY, typeId);
            resolve(status);
          }
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },

  async [types.UPDATE_TYPE_LIBRARY]({ commit, dispatch },
    { workspaceId, type }: {workspaceId: string, type: TypeLibrary}): Promise<any> {
    return new Promise((resolve, reject) => {
      UpdateType(workspaceId, type)
        .then(({ data }) => {
          commit(types.MUTATE_UPDATE_TYPE_LIBRARY, type);
          const msg = i18n.t('type_saved', { typeName: type.name }).toString();
          handleInfoMessage(msg, dispatch);
          resolve(data);
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },

  async [types.RESOLVED_TYPES]({ commit, dispatch }, workspaceId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      ResolvedTypes(workspaceId)
        .then(({ data }) => {
          const typesLibrary: TypeLibrary[] = data;

          // commit(types.MUTATE_RESOLVED_TYPES, typesLibrary);
          resolve(typesLibrary);
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },

  async [types.RESOLVED_TYPE]({ commit, dispatch },
    { workspaceId, typeId }:{ workspaceId: string, typeId: string }): Promise<any> {
    return new Promise((resolve, reject) => {
      ResolvedType(workspaceId, typeId)
        .then(({ data }) => {
          const typesLibrary: TypeLibrary = data;

          // commit(types.MUTATE_RESOLVED_TYPE, typesLibrary);
          resolve(typesLibrary);
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },
};
